/*
  wn-enquiry-klaviyo.js — Bespoke enquiry form: multi-select fix + Klaviyo events + Meta Lead.
  Rewritten 21 Sept 2026 after a customer reported "Error! Something went wrong."
  on every submit. sections/bespoke-enquiry-form.liquid is signed-off copy and
  is NOT touched; this file does the repair on load.

  WHAT BROKE. Shopify replied "Preferred metals is invalid. Preferred stones is
  invalid. Styles they like is invalid." (read with form.errors | default_errors
  on a duplicate theme). Shopify no longer accepts array-named custom fields
  (contact[Preferred metals][]) in the contact form.

  THE FIX (fixMulti). For each checkbox group named contact[X][]:
    - the checkboxes lose their name and gain data-wn-multi="contact[X]"
      (the section's own validate() still sees them as one group),
    - ONE hidden input name="contact[X]" is added to the step and kept equal
      to the ticked labels joined with ", " on every change and at submit.
  That single value is what lands in the enquiry email and in Klaviyo.

  RULES (found the hard way, see context/lessons-learned.md):
  1. Never call preventDefault() + form.submit() here. Shopify's hCaptcha
     script binds its own submit handler and wraps form.submit; hijacking the
     submit put every visitor on the "I am human" challenge page. Let the
     native submit run; the beacons below use keepalive so they survive the
     page leaving.
  2. Read fields by el.name; phone stays an event PROPERTY, never $phone_number.

  STEP 12 (21 Sept). sections/wn-book-after.liquid renders the booking step
  after the success message. This file (a) rewrites the form's "Step n of 11"
  to "of 12" while that section is on the page, and (b) stores first name,
  last name, email and phone in sessionStorage at submit so step 12 can
  pre-fill the Acuity calendar.

  THE GATE (21 Oct). Step 12 now holds the submit back until a time is booked,
  by cancelling the submit event from a capture-phase listener in
  assets/wn-book12.js. That is why the handler at the bottom checks
  defaultPrevented, and why capture() is exposed as window.wnEnquiryCapture:
  the gate banks the answers under its own metric, with no alert to Bianca,
  so nothing is lost if the visitor never books.
*/
(function () {
  "use strict";
  var COMPANY_ID = "Uc4DZa";
  var REVISION = "2024-10-15";
  var METRIC_CUSTOMER = "Bespoke Enquiry Submitted";
  var METRIC_ALERT = "Bespoke Enquiry Alert";
  var ALERT_TO = "info@whitenovember.com.au";

  var host = document.querySelector("[data-wn-steps], [data-wn-enq]");
  if (!host) return;
  // FALLBACK (21 Sept): if Shopify bounced the enquiry, replace the theme's
  // generic "Error! Something went wrong." with a way out. The section prints
  // the generic string from locales; this keeps the signed-off Liquid untouched.
  (function () {
    var box = document.querySelector(".wn-enquiry .alert-message.error");
    if (!box) return;
    box.innerHTML = "Something went wrong and your enquiry did not send. Please email " +
      '<a href="mailto:info@whitenovember.com.au" style="color:inherit;text-decoration:underline">info@whitenovember.com.au</a>' +
      " with a few words about what you have in mind and I will take it from there.";
  })();

  var form = host.closest("form") || host.querySelector("form");
  if (!form || host.hasAttribute("data-wn-enq")) return;

  function syncMulti(name) {
    var field = form.querySelector('input[type="hidden"][data-wn-multi-field="' + name + '"]');
    if (!field) return;
    var picked = [];
    [].forEach.call(form.querySelectorAll('input[type="checkbox"][data-wn-multi="' + name + '"]'), function (inp) {
      if (inp.checked) picked.push(String(inp.value || "").trim());
    });
    field.value = picked.filter(Boolean).join(", ");
  }
  function syncAllMulti() {
    [].forEach.call(form.querySelectorAll("[data-wn-multi-field]"), function (f) {
      syncMulti(f.getAttribute("data-wn-multi-field"));
    });
  }
  function fixMulti() {
    var groups = {};
    [].forEach.call(form.querySelectorAll('input[type="checkbox"]'), function (inp) {
      var n = inp.getAttribute("name") || "";
      if (!/^contact\[.+\]\[\]$/.test(n)) return;
      var base = n.slice(0, -2);
      if (!groups[base]) groups[base] = [];
      groups[base].push(inp);
    });
    Object.keys(groups).forEach(function (base) {
      var first = groups[base][0];
      var hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = base;
      hidden.setAttribute("data-wn-multi-field", base);
      var step = first.closest(".wn-step") || form;
      step.insertBefore(hidden, step.firstChild);
      groups[base].forEach(function (inp) {
        inp.removeAttribute("name");
        inp.setAttribute("data-wn-multi", base);
        inp.addEventListener("change", function () { syncMulti(base); });
      });
      syncMulti(base);
    });
    // capture phase: runs before Shopify's captcha reads the form
    form.addEventListener("submit", syncAllMulti, true);
  }
  fixMulti();

  // STEP 12 counter: the section counts its own steps; add one for booking.
  (function () {
    if (!document.querySelector("[data-wn-book-after]")) return;
    var wrap = host.closest(".wn-enquiry") || document;
    var progress = wrap.querySelector("[data-wn-progress]");
    var bar = wrap.querySelector("[data-wn-bar]");
    var own = host.querySelectorAll(".wn-step").length;
    if (!progress || !own) return;
    function fix() {
      var m = /^Step (\d+) of (\d+)$/.exec(progress.textContent || "");
      if (!m || +m[2] !== own) return;
      var n = +m[1], total = own + 1;
      progress.textContent = "Step " + n + " of " + total;
      if (bar) bar.style.width = (n / total * 100) + "%";
    }
    fix();
    if (window.MutationObserver) new MutationObserver(fix).observe(progress, { childList: true, characterData: true, subtree: true });
  })();

  var FIELDS = [
    ["contact[Their role]", "Their role"],
    ["contact[Is it a surprise]", "Is it a surprise"],
    ["contact[Styles they like]", "Styles they like"],
    ["contact[Other styles]", "Other styles"],
    ["contact[Design approach]", "Design approach"],
    ["contact[Preferred metals]", "Preferred metals"],
    ["contact[Preferred stones]", "Preferred stones"],
    ["contact[Ring size]", "Ring size"],
    ["contact[Budget]", "Budget"],
    ["contact[Ideal ready date]", "Ideal ready date"],
    ["contact[Location]", "Location"],
    ["contact[How they heard about us]", "How they heard about us"],
    ["contact[Extra notes]", "Extra notes"],
    ["contact[Phone]", "Phone"]
  ];

  function readOne(name) {
    var nodes = [].filter.call(form.querySelectorAll("input, select, textarea"), function (el) {
      return el.name === name;
    });
    var picked = [];
    [].forEach.call(nodes, function (el) {
      var t = (el.type || "").toLowerCase();
      if (t === "checkbox" || t === "radio") {
        if (el.checked) picked.push(String(el.value || "").trim());
      } else if (String(el.value || "").trim()) {
        picked.push(String(el.value).trim());
      }
    });
    return picked.filter(Boolean).join(", ");
  }

  function collect() {
    var email = readOne("contact[email]");
    if (!email) return null;
    var props = {};
    FIELDS.forEach(function (pair) {
      var v = readOne(pair[0]);
      if (v) props[pair[1]] = v;
    });
    var first = readOne("contact[First name]");
    var last = readOne("contact[Last name]");
    if (first) props["First name"] = first;
    if (last) props["Last name"] = last;
    props.Email = email;
    props["Enquiry page"] = window.location.pathname;
    props["Meta event id"] = metaEventId();
    return { email: email, first: first, last: last, props: props };
  }

  function sendEvent(metric, profileAttrs, props) {
    var body = { data: { type: "event", attributes: {
      properties: props,
      metric: { data: { type: "metric", attributes: { name: metric } } },
      profile: { data: { type: "profile", attributes: profileAttrs } }
    } } };
    try {
      return fetch("https://a.klaviyo.com/client/events/?company_id=" + COMPANY_ID, {
        method: "POST", mode: "cors", keepalive: true,
        headers: { "Content-Type": "application/json", revision: REVISION },
        body: JSON.stringify(body)
      });
    } catch (e) { return null; }
  }

  function metaEventId() {
    try { if (window.crypto && crypto.randomUUID) return "wn-enq-" + crypto.randomUUID(); } catch (e) {}
    return "wn-enq-" + Date.now() + "-" + Math.random().toString(36).slice(2, 10);
  }
  function metaPixelId() {
    try {
      var st = window.fbq && window.fbq.getState && window.fbq.getState();
      if (st && st.pixels && st.pixels.length && st.pixels[0].id) return st.pixels[0].id;
    } catch (e) {}
    return "152810061798202";
  }
  function metaPhone(raw) {
    var d = String(raw || "").replace(/[^0-9]/g, "");
    if (!d) return "";
    if (d.length === 10 && d.charAt(0) === "0") return "61" + d.slice(1);
    if (d.length === 9 && d.charAt(0) === "4") return "61" + d;
    return d.length >= 10 && d.length <= 15 ? d : "";
  }
  function metaAdvancedMatch(data) {
    try {
      var m = {};
      if (data.email) m.em = String(data.email).trim().toLowerCase();
      var ph = metaPhone(data.props.Phone);
      if (ph) m.ph = ph;
      if (data.first) m.fn = String(data.first).trim().toLowerCase();
      if (data.last) m.ln = String(data.last).trim().toLowerCase();
      var keys = 0;
      for (var k in m) if (Object.prototype.hasOwnProperty.call(m, k)) keys++;
      if (keys) window.fbq("init", metaPixelId(), m);
    } catch (e) {}
  }
  function sendMetaLead(data) {
    try {
      if (typeof window.fbq != "function") return;
      metaAdvancedMatch(data);
      var props = { content_name: "Bespoke enquiry", content_category: "Bespoke" };
      if (data.props.Budget) props.budget_band = data.props.Budget;
      if (data.props["Their role"]) props.enquirer_role = data.props["Their role"];
      window.fbq("track", "Lead", props, { eventID: data.props["Meta event id"] });
    } catch (e) {}
  }

  /* THE CAPTURE, LIFTED OUT OF THE SUBMIT HANDLER (21 Oct 2026).
     Step 12 now holds the form back until a time is booked, so the answers
     have to be bankable BEFORE anything is submitted - otherwise a visitor
     whose calendar will not load (an in-app browser, a blocked iframe) loses
     the lot, which is strictly worse than the drop-off we are fixing.
     wn-book12.js calls this at the gate with a different metric and no alert
     to Bianca; the real submit below still sends the usual two. */
  function capture(metric, alsoAlert) {
    var data;
    try { data = collect(); } catch (err) { return null; }
    if (!data) return null;
    try {
      sessionStorage.setItem("wn-enq-contact", JSON.stringify({
        first: data.first || "", last: data.last || "", email: data.email || "", phone: data.props.Phone || ""
      }));
    } catch (err) {}
    try {
      var k = window.klaviyo, who = { $email: data.email };
      if (data.first) who.$first_name = data.first;
      if (data.last) who.$last_name = data.last;
      if (k && typeof k.identify == "function") k.identify(who);
      else { window.klaviyo = window.klaviyo || []; window.klaviyo.push(["identify", who]); }
    } catch (err) {}
    var customerProfile = { email: data.email };
    if (data.first) customerProfile.first_name = data.first;
    if (data.last) customerProfile.last_name = data.last;
    try {
      sendEvent(metric, customerProfile, data.props);
      if (alsoAlert) sendEvent(METRIC_ALERT, { email: ALERT_TO }, data.props);
    } catch (err) {}
    return data;
  }

  /* The only thing wn-book12.js needs from this file. */
  window.wnEnquiryCapture = capture;

  var fired = false;
  form.addEventListener("submit", function (e) {
    // Fire-and-forget. Do NOT touch e.preventDefault() or form.submit():
    // Shopify's captcha owns the actual send. The step 12 gate cancels this
    // submit from a capture-phase listener, which is why defaultPrevented is
    // checked here rather than anything being cancelled in this file.
    if (fired || e.defaultPrevented) return;
    var data = capture(METRIC_CUSTOMER, true);
    if (!data) return;
    fired = true;
    sendMetaLead(data);
  });
})();